import { useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Grid, Link as MuiLink } from '../../../1-primative';
import { PortfolioReport } from '../../../../interfaces';
import { Badge, Tooltip } from '../../../2-component';
import { formatMoneyValue } from '../../../../util';
import { useLocalization } from '../../../../util/useLocalization';

type ColumnData = { data: PortfolioReport };

const isGoal = (data: PortfolioReport): boolean => data.object?.__typename === 'Goal';

const isSubAccount = (data: PortfolioReport): boolean => data.object?.__typename === 'SubAccount';

const generateClientUrl = (data: PortfolioReport): string => {
  if (isSubAccount(data) && data.object.account?.user) {
    return `/clients/${data.object.account.user.id}`;
  }

  if (isGoal(data) && data.object.householdClientGroup) {
    return `/households/${data.object.householdClientGroup.id}`;
  }

  if (isGoal(data) && data.object.user) {
    return `/clients/${data.object.user.id}`;
  }

  return '';
};

const generateDisplayName = (data: PortfolioReport): string => {
  if (isSubAccount(data) && data.object.account?.user) {
    const { firstName, lastName, entityName } = data.object.account.user;
    if (firstName && lastName) { return `${firstName} ${lastName}`; }
    if (entityName) { return entityName; }
    return '';
  }

  if (isGoal(data) && data.object.householdClientGroup) {
    return data.object.householdClientGroup.name || '';
  }

  if (isGoal(data) && data.object.user) {
    const { firstName, lastName, entityName } = data.object.user;
    if (firstName && lastName) { return `${firstName} ${lastName}`; }
    if (entityName) { return entityName; }
    return '';
  }

  return '';
};

const generateGoalUrl = (data: PortfolioReport): string | null => {
  let clientId: string | undefined;
  let goalId: string | undefined;

  if (isSubAccount(data) && data.object.account.user.id && data.object.goal.id) {
    clientId = data.object.account.user.id;
    goalId = data.object.goal.id;
  } else if (isGoal(data) && data.object.user.id && data.object.id) {
    clientId = data.object.user.id;
    goalId = data.object.id;
  }

  if (clientId && goalId) {
    return `/clients/${clientId}/goal/${goalId}`;
  }

  return null;
};

function generateSubAccountUrl(data: PortfolioReport): string | null {
  if (isSubAccount(data) && data.object.goal.id && data.id && data.object.account.id) {
    const clientId = data.object.account.user.id;
    const goalId = data.object.goal.id;
    const subAccountId = data.id;
    return `/clients/${clientId}/goal/${goalId}/subaccount/${subAccountId}`;
  }
  return null;
}

function randomNumber(min: number, max: number): number {
  if (min > max) { throw new Error('Min must be less than or equal to max'); }
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const ClientColumn = ({ data }: ColumnData): ReactElement => {
  const [link, setLink] = useState<string | null>('');
  const [name, setName] = useState<string | null>('');

  useEffect(() => {
    setLink(generateClientUrl(data));
    setName(generateDisplayName(data));
  }, [data]);

  return (
    <MuiLink component={Link} to={link} target='_blank' onClick={(e: any) => e.stopPropagation()}>
      {name}
    </MuiLink>
  );
};

export const PortfolioTypeColumn = ({ data }: ColumnData): ReactElement => {
  const { t } = useTranslation(['rebalanceReport']);
  const [link, setLink] = useState<string | null>('');

  useEffect(() => {
    setLink(generateSubAccountUrl(data));
  }, [data]);

  return (
    <>
      { link && <MuiLink component={Link} to={link} target='_blank' onClick={(e: any) => e.stopPropagation()}>
        { t(`types.${data.modelPortfolioType}`) }
      </MuiLink> }
      { isGoal(data) && t(`types.${data.modelPortfolioType}`) }
    </>
  );
};

export const GoalColumn = ({ data }: ColumnData): ReactElement => {
  const { t } = useTranslation(['rebalanceReport']);
  const [link, setLink] = useState<string | null>('');

  useEffect(() => {
    setLink(generateGoalUrl(data));
  }, [data]);

  return (
    <>
      {isSubAccount(data) && (
        <MuiLink component={Link} to={link} target='_blank' onClick={(e: any) => e.stopPropagation()}>
          { data.object.goal?.name || t(`goalType.${data.object.goal?.type}`) }
        </MuiLink>)}
      {isGoal(data) && (
        <MuiLink component={Link} to={link} target='_blank' onClick={(e: any) => e.stopPropagation()}>
          { data.object.name || t(`goalType.${data.object.type}`) }
        </MuiLink>)}
    </>
  );
};

export const ModelColumn = ({ data }: ColumnData): ReactElement => (<>{data.modelPortfolio.translatedName?.en}</>);

export const AvailableForCashColumn = ({ data }: ColumnData): ReactElement => {
  const { t } = useTranslation(['rebalanceReport']);

  return (
    <Tooltip title={t('tooltips.cashAvailableForTrade')} placement='top'>
      <span>{formatMoneyValue(data.cashAvailableForTradeCents)}</span>
    </Tooltip>
  );
};

export const ExpectedCashColumn = ({ data }: ColumnData): ReactElement => {
  const { t } = useTranslation(['rebalanceReport']);

  return (
    <Tooltip title={t('tooltips.cashAvailableForTrade')} placement='top'>
      <span>{formatMoneyValue(data.expectedCashCents)}</span>
    </Tooltip>
  );
};

export const TotalMarketValueColumn = ({ data }: ColumnData): ReactElement => (<>{formatMoneyValue(data.object?.statistics?.marketValueCents || 0)}</>);

export const TriggersColumn = ({ data }: ColumnData): ReactElement => (
    <Grid container gap={1} flexWrap={'nowrap'}>
      { (data.transferRebalanceRequired || data.liquidateRebalanceRequired) && <Grid item>
        <Badge label='Cash' color='warning' />
      </Grid> }
      { data.driftRebalanceRequired && <Grid item>
        <Badge label='Drift' color='neutral' />
      </Grid> }
    </Grid>
);

export const SMAIncludedColumn = ({ data }: ColumnData): ReactElement => {
  const random: number = randomNumber(1, 2);
  return (random === 1 ? <CheckCircleIcon color='success' sx={{ fontSize: '12px' }} /> : <></>);
};

export const FundTransferColumn = ({ data }: ColumnData): ReactElement => (<Badge label='In Progress' color='neutral' />);

export const OrganizationColumn = ({ data }: ColumnData): ReactElement => {
  if (isGoal(data)) { return (<>{ data.object.user.organization.name }</>); }
  return (<>{ data.object.account.user.organization.name }</>);
};

export const LastUpdatedColumn = ({ data }: ColumnData): ReactElement => {
  const { localizedDateTime } = useLocalization();
  return (<>{data.updatedAt ? localizedDateTime(data.updatedAt) : ''}</>);
};
